import { useState } from 'react';
import { useConfiguredBrandsQuery } from '../../queries/brandMetrics';
import { previousPeriod } from './periodCompare';
import BrandPanel from './BrandPanel';
import BrandsOverviewTable from './BrandsOverviewTable';
import PanelDateControls from './components/PanelDateControls';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const brandSelectCls =
  'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700';

/**
 * Vista admin del panel. Home = tabla de pacing de todas las marcas;
 * seleccionar una marca (dropdown o click en fila) abre su panel individual.
 */
export default function AdminBrandPanel() {
  const { data: brands = [] } = useConfiguredBrandsQuery();
  const [brandId, setBrandId] = useState(null);
  const [start, setStart] = useState(() => isoDaysAgo(30));
  const [end, setEnd] = useState(() => isoDaysAgo(1));
  const [compareMode, setCompareMode] = useState('previous'); // 'previous' | 'custom'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const priorPeriod = previousPeriod(start, end);
  const compareStart = compareMode === 'custom' ? customStart : priorPeriod.start;
  const compareEnd = compareMode === 'custom' ? customEnd : priorPeriod.end;

  const onCompareModeChange = (mode) => {
    if (mode === 'custom' && !customStart) {
      setCustomStart(priorPeriod.start);
      setCustomEnd(priorPeriod.end);
    }
    setCompareMode(mode);
  };

  const selectedBrand = brands.find((b) => b.id === brandId);

  const dateControls = {
    start,
    end,
    onStartChange: (e) => setStart(e.target.value),
    onEndChange: (e) => setEnd(e.target.value),
    compareMode,
    onCompareModeChange: (e) => onCompareModeChange(e.target.value),
    customStart,
    customEnd,
    onCustomStartChange: (e) => setCustomStart(e.target.value),
    onCustomEndChange: (e) => setCustomEnd(e.target.value),
  };

  const brandSwitcher = (
    <select
      aria-label="Marca"
      className={brandSelectCls}
      value={brandId ?? ''}
      onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">Todas las marcas</option>
      {brands.map((b) => (
        <option key={b.id} value={b.id}>
          {b.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {selectedBrand ? (
        <BrandPanel
          brand={selectedBrand}
          dateControls={dateControls}
          compareStart={compareStart}
          compareEnd={compareEnd}
          onBack={() => setBrandId(null)}
          brandSwitcher={brandSwitcher}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Panel de marcas</h1>
              <p className="text-sm text-gray-500">
                Datos sincronizados 2 veces por día — sin llamadas a las plataformas.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {brandSwitcher}
              <PanelDateControls controls={dateControls} />
            </div>
          </div>
          <BrandsOverviewTable start={start} end={end} onSelectBrand={setBrandId} />
        </>
      )}
    </div>
  );
}
